import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'ts-debounce';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { useArchiveQRCodeApi, useSearchQRCodesApi } from '../api';
import { EntityQRCodeParametersSimpleView, QRCodeSearchSort, QRCodeType } from '../types';

type ListResponse = { items: EntityQRCodeParametersSimpleView[]; count: number };

type FilterState = {
  page: number;
  limit: number;
  offset: number;
  sort?: string;
  query?: string;
  type?: string;
};

const defaultFilter: FilterState = {
  page: 0,
  limit: 25,
  offset: 0,
  sort: 'dateCreated_desc',
  query: '',
  type: '',
};

const mapUiSortToServer = (uiSort?: string): string | undefined => {
  if (!uiSort) return undefined;
  const [field, order] = uiSort.split('_');
  const fieldMap: Record<string, string> = {
    qrcodeTypeName: 'type',
    dateCreated: 'date_created',
    dateUpdated: 'date_updated',
  };
  return `${fieldMap[field] || field}_${order}`;
};

export const useQRCodesViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const api = useSearchQRCodesApi();
  const archiveApi = useArchiveQRCodeApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [list, setList] = useState<ListResponse | undefined>(undefined);
  const filterRef = useRef<FilterState>(defaultFilter);

  const load = useCallback(async (currentFilter: FilterState) => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      const response = await api({
        companyId: currentCompanyId,
        limit: currentFilter.limit,
        offset: currentFilter.offset,
        sort: (mapUiSortToServer(currentFilter.sort) as QRCodeSearchSort) || 'date_created_desc',
        query: currentFilter.query || undefined,
        type: currentFilter.type as QRCodeType | undefined,
      });
      setList(response.data);
    } catch (error) {
      reportErrorToUser(error, 'Could not load the QR codes.');
    } finally {
      setLoading(false);
    }
  }, [api, currentCompanyId]);

  const initLoad = useCallback(async () => {
    if (!currentCompanyId) return;
    await load(defaultFilter);
  }, [currentCompanyId, load]);

  const reloadQRCodes = useCallback(async (overrideFilter?: Partial<FilterState>) => {
    const currentFilter = overrideFilter ? { ...filterRef.current, ...overrideFilter } : filterRef.current;
    await load(currentFilter);
  }, [load]);

  const reloadQRCodesDebounce = useMemo(() => debounce(reloadQRCodes, 500), [reloadQRCodes]);

  const changeSorting = useCallback(async (s: GridSortModel) => {
    if (!s || s.length === 0) return;
    const newSort = `${s[0].field}_${s[0].sort}`;
    const newFilter = { sort: newSort };
    setFilter(prev => ({ ...prev, ...newFilter }));
    await reloadQRCodes(newFilter);
  }, [reloadQRCodes]);

  const changePagination = useCallback(async (p: GridPaginationModel) => {
    const newFilter = {
      page: p.page,
      limit: p.pageSize,
      offset: p.page * p.pageSize,
    };
    setFilter(prev => ({ ...prev, ...newFilter }));
    await reloadQRCodes(newFilter);
  }, [reloadQRCodes]);

  const changeSearchFilter = useCallback(async (_: ChangeEvent<HTMLInputElement>, query: string) => {
    const newFilter = { query, page: 0, offset: 0 };
    setFilter(prev => ({ ...prev, ...newFilter }));
    await reloadQRCodesDebounce(newFilter);
  }, [reloadQRCodesDebounce]);

  const archiveQRCode = useCallback(async (id: string) => {
    if (!currentCompanyId) return;
    try {
      await archiveApi(id, currentCompanyId);
    } catch (error) {
      reportErrorToUser(error, 'Could not archive the QR code.');
      return;
    }
    // The archived code may have been the only one on the last page, which then holds nothing to
    // show: reading the list from the first page keeps the table and its count in step.
    const newFilter = { page: 0, offset: 0 };
    setFilter(prev => ({ ...prev, ...newFilter }));
    await reloadQRCodes(newFilter);
  }, [archiveApi, currentCompanyId, reloadQRCodes]);

  const changeTypeFilter = useCallback(async (value: string) => {
    const newFilter = { type: value, page: 0, offset: 0 };
    setFilter(prev => ({ ...prev, ...newFilter }));
    await reloadQRCodes(newFilter);
  }, [reloadQRCodes]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    initLoad();
  }, [initLoad]);

  return {
    isPageLoading: loading,
    filter,
    list,
    changeSorting,
    changePagination,
    changeSearchFilter,
    changeTypeFilter,
    archiveQRCode,
  };
};


